<script setup>
import { ref, computed, onMounted } from 'vue';
import { useKanbanStore } from '@/stores/kanban';
import ModalTask from './modals/ModalTask.vue';
import ModalCreateTask from './modals/ModalCreateTask.vue';
import KanbanColumnWeekly from './columns/KanbanColumnWeekly.vue';
import KanbanColumnWeekend from './columns/KanbanColumnWeekend.vue';
import KanbanColumnBoard from './columns/KanbanColumnBoard.vue';
import moment from 'moment';

const kanbanStore = useKanbanStore();

const ModalTaskRef = ref(null);
const ModalCreateTaskRef = ref(null);

const dataAtual = ref(new Date());
const draggedTask = ref(null);
const sourceDay = ref(null);
const dateNow = ref('');
const dateSearch = ref('');
const viewMode = ref('weekly');

const loading = computed(() => kanbanStore.loading);
const days = computed(() => kanbanStore.days);

const tasksByStatus = computed(() => {
  const allTasks = Object.values(days.value).flat();
  return {
    start: allTasks.filter(t => String(t.situation) === '1'),
    executed: allTasks.filter(t => String(t.situation) === '2'),
    bloqued: allTasks.filter(t => String(t.situation) === '4'),
    finish: allTasks.filter(t => String(t.situation) === '3')
  };
});

const startOfWeek = computed(() => moment(dateSearch.value || new Date()).startOf('isoWeek'));

const formatDateWeek = computed(() => {
  const weekdays = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta'];
  return weekdays.reduce((acc, day) => {
    acc[day] = days.value[day] || [];
    return acc;
  }, {});
});

const isCurrentWeek = computed(() => moment().startOf('isoWeek').format('YYYY-MM-DD') === moment(startOfWeek.value).format('YYYY-MM-DD'));

const getDateForDay = (index) => startOfWeek.value.clone().add(index, 'days').format('DD');
const isToday = (index) => startOfWeek.value.clone().add(index, 'days').format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
const getDate = (index) => startOfWeek.value.clone().add(index, 'days').format('YYYY-MM-DD');
const getDatebr = (index) => startOfWeek.value.clone().add(index, 'days').format('YYYY/MM/DD');

const getTask = async (clearCurrent = false) => {
  await kanbanStore.fetchTasks(moment(dateSearch.value).format('YYYY-MM-DD'), clearCurrent);
};

const selectDay = (task) => ModalTaskRef.value?.openTask(task);
const CreateTask = (index) => ModalCreateTaskRef.value?.openCreateTask(getDate(index), 'task');
const CreateTaskStatus = () => ModalCreateTaskRef.value?.openCreateTask(new Date(), 'task');

const onDragStart = (task, day) => {
  draggedTask.value = task;
  sourceDay.value = day;
};

const onDropColumn = async (targetDay, index) => {
  if (!draggedTask.value) return;

  try {
    await kanbanStore.updateTask(draggedTask.value.id, {
      date: getDatebr(index)
    });
    await getTask();
  } catch (error) {
    console.error('Erro ao mover tarefa:', error);
  } finally {
    draggedTask.value = null;
    sourceDay.value = null;
  }
};

const onDropStatus = async (targetSituation) => {
  if (!draggedTask.value) return;

  try {
    await kanbanStore.updateTask(draggedTask.value.id, {
      situation: targetSituation
    });
    await getTask();
  } catch (error) {
    console.error('Erro ao mudar status da tarefa:', error);
  } finally {
    draggedTask.value = null;
    sourceDay.value = null;
  }
};

const getSemanaDoMes = (data) => {
  const m = moment(data).locale('pt-br');
  dateSearch.value = m.toDate();
  const semana = Math.ceil(m.date() / 7);

  dateNow.value = `${semana}ª semana de ${m.format('MMMM')}/${m.format('YYYY')}`;
  getTask(true);
};

const avancarSemana = () => { dataAtual.value.setDate(dataAtual.value.getDate() + 7); getSemanaDoMes(dataAtual.value); };
const voltarSemana = () => { dataAtual.value.setDate(dataAtual.value.getDate() - 7); getSemanaDoMes(dataAtual.value); };
const resetWeeks = () => { dataAtual.value = new Date(); getSemanaDoMes(dataAtual.value); };

onMounted(() => {
  getSemanaDoMes(dataAtual.value);
});
</script>

<template>
  <div class="w-100 font-docs">
    <ModalTask ref="ModalTaskRef" @taskDescription="getTask" />
    <ModalCreateTask ref="ModalCreateTaskRef" @CreateTask="getTask" />

    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center">
        <i class="fa-solid fa-chevron-left mr-2 text-muted cursor-pointer" style="font-size: 18px" @click="voltarSemana"></i>
        <i class="fa-solid fa-chevron-right mr-3 text-muted cursor-pointer" style="font-size: 18px" @click="avancarSemana"></i>
        <span class="font-weight-bold text-dark mr-3" style="font-size: 20px">{{ dateNow }}</span>
        
        <div class="d-flex align-items-center cursor-pointer" @click="resetWeeks">
          <span :class="['mr-1', isCurrentWeek ? 'text-dark font-weight-bold' : 'text-primary-custom']" style="font-size: 15px">
            Semana atual
          </span>
          <i :class="['fa-solid fa-arrows-rotate', loading ? 'fa-spin' : '', isCurrentWeek ? 'text-dark' : 'text-primary-custom']" style="font-size: 14px"></i>
        </div>
      </div>

      <div class="kanban-view-toggle">
        <button :class="['kanban-view-btn', viewMode === 'weekly' ? 'kanban-view-btn--active' : '']" @click="viewMode = 'weekly'">
          <i class="fa-regular fa-calendar mr-2"></i> Visão semanal
        </button>
        <button :class="['kanban-view-btn', viewMode === 'board' ? 'kanban-view-btn--active' : '']" @click="viewMode = 'board'">
          <i class="fa-solid fa-list-check mr-2"></i> Quadro de tarefas
        </button>
      </div>
    </div>

    <div class="kanban-content-wrapper position-relative">
      <transition name="fade-loading">
        <div v-if="loading" class="kanban-loading-overlay">
          <spinnerprime style="width: 44px; height: 44px;" strokeWidth="3" />
        </div>
      </transition>

      <transition name="fade-slide" mode="out-in">
        <div v-if="viewMode === 'weekly'" key="weekly" class="kanban-grid-weekly">
          <KanbanColumnWeekly
            v-for="(tasks, day, index) in formatDateWeek"
            :key="day"
            :day-label="day"
            :day-number="getDateForDay(index)"
            :day-index="index"
            :is-today="isToday(index)"
            :tasks="tasks"
            @create-task="CreateTask"
            @select-task="selectDay"
            @dragstart="onDragStart"
            @drop-column="onDropColumn"
          />

          <KanbanColumnWeekend
            :saturday-tasks="days.Sábado || []"
            :sunday-tasks="days.Domingo || []"
            :saturday-date="getDateForDay(5)"
            :sunday-date="getDateForDay(6)"
            :is-saturday-today="isToday(5)"
            :is-sunday-today="isToday(6)"
            @create-task="CreateTask"
            @select-task="selectDay"
            @dragstart="onDragStart"
            @drop-column="onDropColumn"
          />
        </div>

        <div v-else-if="viewMode === 'board'" key="board" class="kanban-board-grid">
          <KanbanColumnBoard
            title="À INICIAR"
            status-value="1"
            icon-class="fa-regular fa-circle"
            header-color="#6C757D"
            :tasks="tasksByStatus.start"
            @create-task="CreateTaskStatus"
            @select-task="selectDay"
            @dragstart="onDragStart"
            @drop-status="onDropStatus"
          />
          <KanbanColumnBoard
            title="EM ANDAMENTO"
            status-value="2"
            icon-class="fa-solid fa-spinner"
            header-color="#087990"
            :tasks="tasksByStatus.executed"
            @create-task="CreateTaskStatus"
            @select-task="selectDay"
            @dragstart="onDragStart"
            @drop-status="onDropStatus"
          />
          <KanbanColumnBoard
            title="BLOQUEADA"
            status-value="4"
            icon-class="fa-solid fa-ban"
            header-color="#842029"
            :tasks="tasksByStatus.bloqued"
            @create-task="CreateTaskStatus"
            @select-task="selectDay"
            @dragstart="onDragStart"
            @drop-status="onDropStatus"
          />
          <KanbanColumnBoard
            title="CONCLUÍDA"
            status-value="3"
            icon-class="fa-solid fa-circle-check"
            header-color="rgb(9 167 6)"
            :tasks="tasksByStatus.finish"
            @create-task="CreateTaskStatus"
            @select-task="selectDay"
            @dragstart="onDragStart"
            @drop-status="onDropStatus"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped>
.kanban-view-toggle {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #F1860B;
  border-radius: 8px;
  overflow: hidden;
  background-color: #FAFAFA;
  height: clamp(36px, 5vh, 42px);
  box-sizing: border-box;
}

.kanban-view-btn {
  flex: 1 1 0;
  min-width: clamp(125px, 9vw, 155px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0 clamp(10px, 1vw, 16px);
  font-size: clamp(12px, 1.6vh, 13px);
  font-weight: 600;
  color: #F1860B;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.kanban-view-btn:hover:not(.kanban-view-btn--active) {
  background-color: rgba(241, 134, 11, 0.08);
}

.kanban-view-btn--active {
  background-color: #F1860B !important;
  color: #FFFFFF !important;
}

.kanban-grid-weekly {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.kanban-board-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}

.text-primary-custom { color: #F1860B !important; }
.cursor-pointer { cursor: pointer; }

.kanban-content-wrapper {
  position: relative;
  min-height: 480px;
}

.kanban-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(248, 249, 250, 0.72);
  backdrop-filter: blur(2px);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.kanban-loading-text {
  font-size: 13px;
  color: #6C757D;
}

:deep(.p-progress-spinner-circle) {
  stroke: #F1860B !important;
}

.fade-loading-enter-active,
.fade-loading-leave-active {
  transition: opacity 0.2s ease;
}

.fade-loading-enter-from,
.fade-loading-leave-to {
  opacity: 0;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 991px) {
  .d-flex.align-items-center.justify-content-between.mb-3 {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
  }

  .kanban-view-toggle {
    width: 100%;
  }

  .kanban-view-btn {
    flex: 1;
    min-width: 0;
    justify-content: center;
    padding: 0 8px;
  }

  .kanban-grid-weekly,
  .kanban-board-grid {
    display: flex !important;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 12px;
    gap: 12px;
  }

  .kanban-grid-weekly > *,
  .kanban-board-grid > * {
    min-width: 280px !important;
    max-width: 320px !important;
    flex-shrink: 0;
    scroll-snap-align: start;
  }
}
</style>